import { useState } from "react";
import DOMPurify from "dompurify";

type Breakpoint = "desktop" | "mobile";

interface PreviewPaneProps {
  html: string;
  onCollapse: () => void;
}

export function PreviewPane({ html, onCollapse }: PreviewPaneProps) {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("desktop");

  const toggle = () =>
    setBreakpoint((b) => (b === "desktop" ? "mobile" : "desktop"));

  return (
    <div className="flex flex-col h-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 shrink-0">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Preview
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Toggle mobile / desktop breakpoint"
          >
            {breakpoint === "desktop" ? "Desktop" : "Mobile"}
          </button>
          <button
            onClick={onCollapse}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            title="Collapse preview"
            aria-label="Collapse preview pane"
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-white dark:bg-gray-900">
        <div
          className={
            breakpoint === "mobile"
              ? "mx-auto w-[375px] border border-dashed border-gray-300 dark:border-gray-600"
              : undefined
          }
        >
          <article
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        </div>
      </div>
    </div>
  );
}
